import {
  Home,
  LayoutDashboard,
  BookOpen,
  Send,
  ArrowDown,
  Settings2,
  Smartphone,
  Puzzle,
} from "lucide-react";
import { PageLayout } from "@/components/PageLayout";
import { PageHero } from "@/components/blocks/PageHero";
import { SectionHeading } from "@/components/blocks/SectionHeading";
import { CTABand } from "@/components/blocks/CTABand";
import { Reveal } from "@/components/Reveal";
import { DemoApp } from "@/components/demo/DemoApp";
import { SourceBadge } from "@/components/demo/ui";
import { ROUTE_CONTACT } from "@/lib/brand";
import {
  CUSTOM_DELIVERABLES,
  CUSTOM_DELIVERABLES_TITLE,
  LEVELS,
  SOURCES,
} from "@/lib/measurement";

const SIDEBAR_ITEMS = [
  { id: "top", label: "Start", icon: Home },
  { id: "demo", label: "Demo", icon: LayoutDashboard },
  { id: "messlogik", label: "So lest ihr die Zahlen", icon: BookOpen },
  { id: "cta", label: "Kontakt", icon: Send },
];

const INTRO_STEPS = [
  {
    icon: Settings2,
    title: "Setup",
    text: "Unternehmen und Leistungen wählen, so wie beim Abschluss eines echten Sponsorings.",
  },
  {
    icon: LayoutDashboard,
    title: "Dashboard",
    text: "Sehen, was daraus wird: Nachweise, Zuschauer, Scans und Ergebnisse.",
  },
  {
    icon: Smartphone,
    title: "Erfassung",
    text: "Als Verein ein Heimspiel eintragen und zusehen, wie sich das Dashboard ändert.",
  },
];

const StepsCard = () => (
  <div className="relative">
    <div className="rounded-[2rem] border border-white/10 backdrop-blur-xl p-7 md:p-8">
      <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-white/40">In drei Schritten</span>
      <ol className="mt-6 space-y-5">
        {INTRO_STEPS.map((s, i) => {
          const Icon = s.icon;
          return (
            <li key={s.title} className="flex items-start gap-4">
              <span className="relative flex items-center justify-center w-11 h-11 shrink-0 rounded-xl bg-white/[0.06] border border-white/10">
                <Icon className="w-[18px] h-[18px] text-brand-orange" />
                <span className="absolute -top-2 -right-2 flex items-center justify-center w-5 h-5 rounded-full bg-navy-950 border border-white/15 text-[10px] font-bold text-white/80">
                  {i + 1}
                </span>
              </span>
              <span>
                <span className="block font-display font-bold text-lg text-white">{s.title}</span>
                <span className="block mt-0.5 text-sm leading-relaxed text-white/55">{s.text}</span>
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  </div>
);

export default function Demo() {
  return (
    <PageLayout sidebarItems={SIDEBAR_ITEMS} title="Demo" testId="demo-main">
      <PageHero
        eyebrow="Demo mit Beispieldaten"
        titleLines={["Vom Setup zur", "Auswertung in"]}
        titleAccent="drei Schritten."
        lead="Stellt ein Beispiel-Sponsoring zusammen und seht, was daraus entsteht: das Dashboard für das Unternehmen und die Eintragungsseite für den Verein. Ohne Anmeldung, alle Werte sind Beispieldaten."
        primary={{ label: "Demo starten", href: "#demo", icon: ArrowDown }}
        secondary={{ label: "So lest ihr die Zahlen", href: "#messlogik" }}
      >
        <StepsCard />
      </PageHero>

      <section
        id="demo"
        data-testid="demo-app-section"
        className="relative py-16 lg:py-24 border-t border-white/[0.06]"
      >
        <div className="pointer-events-none absolute inset-0 overflow-hidden -z-10">
          <div className="absolute top-40 -left-32 w-[560px] h-[460px] rounded-full bg-brand-orange/[0.07] blur-[170px]" />
          <div className="absolute bottom-40 -right-32 w-[560px] h-[460px] rounded-full bg-brand-blue/[0.08] blur-[170px]" />
        </div>
        <div className="px-6 md:px-10 lg:px-16">
          <DemoApp />
        </div>
      </section>

      <section
        id="messlogik"
        data-testid="demo-logic-section"
        className="relative py-24 lg:py-32 border-t border-white/[0.06]"
      >
        <div className="px-6 md:px-10 lg:px-16">
          <SectionHeading
            eyebrow="So lest ihr die Zahlen"
            title="Jede Zahl sagt, "
            accent="woher sie kommt"
            tail="."
            lead="Das Dashboard ordnet alles in drei Ebenen und kennzeichnet bei jedem Wert, wie er entstanden ist. So weiß jede Seite, worauf sie sich verlassen kann."
          />

          <div className="grid gap-5 md:gap-6 md:grid-cols-3">
            {LEVELS.map((l, i) => {
              const Icon = l.icon;
              return (
                <Reveal key={l.title} delay={i * 0.07}>
                  <div className="group relative h-full overflow-hidden rounded-3xl border border-white/10 backdrop-blur-xl p-8 hover:border-white/20 transition-colors duration-500">
                    <div className="absolute -right-3 -top-6 font-display font-black text-[6rem] leading-none text-white/[0.03] group-hover:text-white/[0.06] transition-colors duration-500 select-none">
                      {l.no}
                    </div>
                    <div className="relative">
                      <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-white/[0.06] border border-white/10 mb-6">
                        <Icon className="w-5 h-5 text-brand-orange" />
                      </span>
                      <h3 className="font-display font-bold text-2xl text-white mb-3">{l.title}</h3>
                      <p className="text-base leading-relaxed text-white/55">{l.text}</p>
                    </div>
                    <div className="absolute bottom-0 left-0 h-1 w-0 bg-gradient-to-r from-brand-orange to-brand-blue group-hover:w-full transition-all duration-700" />
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-6 rounded-3xl border border-white/10 backdrop-blur-xl p-8 md:p-10">
              <div className="grid gap-8 md:grid-cols-3">
                {SOURCES.map((s) => (
                  <div key={s.kind}>
                    <SourceBadge kind={s.kind} proof={s.proof} />
                    <p className="mt-4 text-sm leading-relaxed text-white/55">{s.text}</p>
                  </div>
                ))}
              </div>
              <p className="mt-8 pt-7 border-t border-white/10 text-sm leading-relaxed text-white/45">
                Die Beispieldaten beschreiben einen Fußballverein der Kreisliga mit rund 150 Zuschauern je Heimspiel.
                Sie sind bewusst realistisch gewählt: kleine, ehrliche Zahlen statt beeindruckender, die sich in der
                Praxis nicht halten lassen.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-6 flex flex-col md:flex-row md:items-center gap-6 rounded-3xl border border-white/10 backdrop-blur-xl p-8 md:p-10">
              <span className="flex items-center justify-center w-12 h-12 shrink-0 rounded-xl bg-white/[0.06] border border-white/10">
                <Puzzle className="w-5 h-5 text-brand-blue" />
              </span>
              <div>
                <h3 className="font-display font-bold text-xl md:text-2xl text-white mb-2">
                  {CUSTOM_DELIVERABLES_TITLE}
                </h3>
                <p className="max-w-4xl text-base leading-relaxed text-white/60">
                  {CUSTOM_DELIVERABLES} In der Demo probiert ihr das im Setup unter „Sonstige Leistung“ aus.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <CTABand
        title="Wollt ihr das mit echten Zahlen "
        accent="ausprobieren"
        tail="?"
        lead="Schreibt uns, was ihr vorhabt. Wir zeigen euch, wie das für euer Sponsoring aussehen würde."
        primary={{ label: "Kontakt aufnehmen", to: ROUTE_CONTACT }}
        secondary={{ label: "Preise ansehen", to: "/preisstruktur" }}
      />
    </PageLayout>
  );
}
